import { onCLS, onINP } from 'web-vitals/attribution';
import { trackGA4Event } from '@gumtree/shared/src/util/ga4-shared';

export type PageType = 'ResultsBrowse' | 'ResultsSearch' | 'VIP' | 'Home' | 'PostAd' | 'EditAd' | string; // 允许监控的页面类型（只在这些页面上报 CLS 和 INP）
const ALLOWED_PAGE_TYPES = ['ResultsBrowse', 'ResultsSearch', 'VIP'] as const;

interface WebVitalsReportData {
    metric: 'CLS' | 'INP';
    value: number;
    rating: 'good' | 'needs-improvement' | 'poor';
    pathname: string;
    pageType?: string;
    deviceType?: string;
    timestamp: number;
    userAgent?: string;
    viewport?: string;
    largestShiftTarget?: string;
    attribution?: string;
    // INP breakdown timings (only when metric === 'INP', have value )
    inputDelay?: number;
    processingDuration?: number;
    presentationDelay?: number;
}

export interface WebVitalsMonitorOptions {
    pageType?: string;
    deviceType?: string;
    clsThreshold?: number;
    inpThreshold?: number;
    enableDebugLog?: boolean;
}

function reportWebVitals(data: WebVitalsReportData): void {
    if (typeof window === 'undefined') {
        return;
    }

    try {
        const eventData: any = {
            event: 'web_vitals',
            metric: data.metric,
            value: data.value,
            rating: data.rating,
            pathname: data.pathname,
            deviceType: data.deviceType,
            userAgent: data.userAgent,
            viewport: data.viewport,
            largestShiftTarget: data.largestShiftTarget,
            attribution: data.attribution,
        };

        // INP specific breakdown timings
        if (data.metric === 'INP') {
            eventData.inputDelay = data.inputDelay;
            eventData.processingDuration = data.processingDuration;
            eventData.presentationDelay = data.presentationDelay;
        }

        trackGA4Event(eventData);
    } catch (error) {
        console.warn('Failed to report Web Vitals:', error);
    }
}

function getElementString(element: Element | null): string {
    if (!element) {
        return 'unknown';
    }

    const tagName = element.tagName?.toLowerCase() || 'unknown';
    const id = element.id ? `#${element.id}` : '';
    const className = element.className && typeof element.className === 'string' 
        ? `.${element.className.split(' ').filter(Boolean).join('.')}` 
        : '';

    return `${tagName}${id}${className}`;
}

function isPageAllowed(pageType: string | undefined): boolean {
    if (!pageType) {
        return false;
    }

    return ALLOWED_PAGE_TYPES.includes(pageType as any);
}

function initCLSMonitor(options: WebVitalsMonitorOptions, pageType: string, deviceType?: string): void {
    const { clsThreshold = 0.1, enableDebugLog = false } = options;

    try {
        onCLS((metric: any) => {
            const { value, rating, attribution, entries } = metric;

            if (value <= clsThreshold) {
                if (enableDebugLog) {
                    console.log(`[Web Vitals] CLS ${value.toFixed(4)} below threshold ${clsThreshold}`);
                }
                return;
            }

            const allShiftSelectors: string[] = [];
            if (entries && Array.isArray(entries)) {
                for (const entry of entries) {
                    if (entry.sources && Array.isArray(entry.sources)) {
                        for (const source of entry.sources) {
                            if (source.node) {
                                const elementString = getElementString(source.node);
                                if (elementString && !allShiftSelectors.includes(elementString)) {
                                    allShiftSelectors.push(elementString);
                                }
                            }
                        }
                    }
                }
            }
            const attributionString = allShiftSelectors.join(', ');

            const reportData: WebVitalsReportData = {
                metric: 'CLS',
                value: value.toFixed(4),
                rating,
                pathname: window.location.pathname,
                pageType,
                deviceType,
                timestamp: Date.now(),
                userAgent: navigator.userAgent,
                viewport: `${window.innerWidth} x ${window.innerHeight}`,
                largestShiftTarget: attribution?.largestShiftTarget || '',
                attribution: attributionString,
            };

            if (enableDebugLog) {
                console.group(`🚨 CLS Detected: ${value.toFixed(4)} (${rating})`, reportData);
            }

            reportWebVitals(reportData);

        }, { reportAllChanges: false });
    } catch (error) {
        console.error('[Web Vitals] Failed to load web-vitals library for CLS:', error);
    }
}

function initINPMonitor(options: WebVitalsMonitorOptions, pageType: string, deviceType?: string): void {
    const { inpThreshold = 200, enableDebugLog = false } = options;

    try {
        onINP((metric: any) => {
            const { value, rating, attribution } = metric;

            if (enableDebugLog) {
                console.log('[Web Vitals] INP metric received:', metric);
            }

            if (value <= inpThreshold) {
                if (enableDebugLog) {
                    console.log(`[Web Vitals] INP ${value}ms below threshold ${inpThreshold}ms`);
                }
                return;
            }

            // 提取 INP 三个时间段的详细数据
            // English comments: Extract the detailed data of the three time periods of INP
            const inputDelay = attribution?.inputDelay || 0;
            const processingDuration = attribution?.processingDuration || 0;
            const presentationDelay = attribution?.presentationDelay || 0;

            const reportData: WebVitalsReportData = {
                metric: 'INP',
                value: value.toFixed(4),
                rating,
                pathname: window.location.pathname,
                pageType,
                deviceType,
                timestamp: Date.now(),
                userAgent: navigator.userAgent,
                viewport: `${window.innerWidth} x ${window.innerHeight}`,
                attribution: attribution?.interactionTarget || '',
                // INP breakdown
                inputDelay: Number(inputDelay.toFixed(2)),
                processingDuration: Number(processingDuration.toFixed(2)),
                presentationDelay: Number(presentationDelay.toFixed(2)),
            };

            if (enableDebugLog) {
                console.group(`⚡ INP Detected: ${value.toFixed(0)}ms (${rating})`);
                console.log('Total INP:', value.toFixed(0), 'ms');
                console.log('  → Input Delay:', inputDelay.toFixed(0), 'ms');
                console.log('  → Processing Duration:', processingDuration.toFixed(0), 'ms');
                console.log('  → Presentation Delay:', presentationDelay.toFixed(0), 'ms');
                console.log('Interaction Target:', getElementString(attribution?.interactionTarget));
                console.groupEnd();
            }

            reportWebVitals(reportData);
        }, { reportAllChanges: true });
    } catch (error) {
        console.error('[Web Vitals] Failed to initialize INP monitor:', error);
    }
}

export function initWebVitalsMonitor(options: WebVitalsMonitorOptions = {}): void {
    if (typeof window === 'undefined') {
        return;
    }

    const { pageType, deviceType } = options;

    if (!isPageAllowed(pageType)) {
        return;
    }
    initCLSMonitor(options, pageType!, deviceType);
    initINPMonitor(options, pageType!, deviceType);
}
